import React from 'react';
import { usePWAInstall } from '@/lib/usePWAInstall';
import { useToast } from '@/components/Toast';

// Persistent nav pill — visible on every screen size, not just desktop, so
// there's always a way to install even after the dismissible banner (below)
// has been dismissed for the session. Collapses to icon-only on narrow
// screens so it doesn't crowd the mobile nav bar.
export const InstallAppButton = ({ className = '', variant = 'pill', onDone }) => {
  const { canInstall, needsManualInstall, promptInstall } = usePWAInstall();
  const { showToast } = useToast();

  if (!canInstall && !needsManualInstall) return null;

  const handleClick = async () => {
    if (needsManualInstall) {
      showToast('Tap the Share icon, then "Add to Home Screen" to install DotLink.', 'info');
      return;
    }
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      showToast('DotLink installed! Find it on your home screen.', 'success');
    }
  };

  // Full-width row for use inside the mobile overflow menu.
  if (variant === 'menu') {
    return (
      <button
        onClick={() => { onDone?.(); handleClick(); }}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 text-left ${className}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
        </svg>
        Install app
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      title="Install app"
      className={`btn-press flex items-center gap-1.5 rounded-full border-2 border-indigo-600 w-10 h-10 sm:w-auto sm:px-4 sm:py-2 justify-center text-sm font-semibold text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors shrink-0 ${className}`}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
        <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
      </svg>
      <span className="hidden sm:inline">Install App</span>
    </button>
  );
};

// Larger dismissible banner for first-run surfaces (dashboard empty state, etc).
// Dismissal is per-session (sessionStorage), not persisted — as long as the
// app isn't installed, it nudges again on the next visit, since missing the
// install is what silently breaks call/message alerts while the app is closed.
export const InstallAppBanner = () => {
  const { canInstall, needsManualInstall, promptInstall } = usePWAInstall();
  const { showToast } = useToast();
  const [dismissed, setDismissed] = React.useState(
    () => sessionStorage.getItem('dl_install_banner_dismissed') === '1'
  );

  if ((!canInstall && !needsManualInstall) || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem('dl_install_banner_dismissed', '1');
    setDismissed(true);
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      showToast('DotLink installed! Find it on your home screen.', 'success');
    }
    dismiss();
  };

  return (
    <div className="animate-fade-up relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 to-purple-600 p-5 sm:p-6 text-white shadow-lg mb-6 flex items-center gap-4">
      <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold">Install DotLink for instant call alerts</p>
        <p className="text-sm text-indigo-100">
          {needsManualInstall
            ? 'Tap the Share icon, then "Add to Home Screen" — so you get alerts even with the browser closed.'
            : 'Get notified the moment someone scans your QR code — even with the browser closed.'}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!needsManualInstall && (
          <button
            onClick={handleInstall}
            className="btn-press bg-white text-indigo-700 rounded-full px-4 py-2 text-sm font-semibold shadow"
          >
            Install
          </button>
        )}
        <button
          onClick={dismiss}
          className="text-white/70 hover:text-white text-sm px-2"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
};
